<script lang="ts">
  // Live server search over the issues-only-safe companies lookup
  // (companiesLookupApi -> GET /companies/lookup), modeled on
  // PersonCombobox's debounced-query skeleton: typing debounces into a fresh
  // ?q= search rather than filtering a caller-prefetched pool. The lookup
  // endpoint (not the full companiesApi CRUD list) is used deliberately —
  // it's the one company read a field-technician (issues-only) account is
  // allowed, so this combobox works identically for every account type. When
  // nothing typed matches an existing company exactly, a final "+ Add ..."
  // option opens a small confirm dialog that quick-creates the company (POST
  // /api/companies/quick, also open to issues_only accounts) and selects it.
  import { createQuery, createMutation, useQueryClient } from '@tanstack/svelte-query';
  import { ChevronDown, Plus } from 'lucide-svelte';
  import { companiesLookupApi, companiesQuickCreateApi } from '$lib/api/crm';
  import { getErrorMessage } from '$lib/api/client';
  import Modal from './Modal.svelte';
  import type { CompanyLookup } from '$lib/types';

  let {
    value = $bindable(undefined),
    selectedKind = $bindable(undefined),
    // Kept in lockstep with `value` alongside selectedKind — lets a caller
    // that searches across more than one vertical at once (no `verticalId`
    // passed, e.g. EmailsPage's admin "track domain" picker) read off which
    // vertical the picked company belongs to without a second round trip.
    selectedVerticalId = $bindable(undefined),
    // Ditto, the picked company's display name — handy when a caller wants
    // to reference "the company you just picked" in surrounding copy without
    // re-deriving it from a separate list.
    selectedName = $bindable(undefined),
    companies = [],
    verticalId,
    kind,
    defaultKind = kind ?? 'company',
    onSelect,
    id,
    placeholder = 'Search or add a company…'
  }: {
    value: number | undefined;
    selectedKind?: CompanyLookup['kind'];
    selectedVerticalId?: number;
    selectedName?: string;
    // A pool the caller already fetched (e.g. IssuesPage's companiesQuery) —
    // used only to resolve an externally-set `value` to a display name
    // without an extra round trip. Never drives the dropdown itself.
    companies?: CompanyLookup[];
    // Left unset to search across every vertical the caller can see (e.g.
    // an admin who doesn't know the company's vertical up front).
    verticalId?: number;
    // Restricts both the live search and quick-create to a single company
    // kind (e.g. the Hotels people form's separate Hotel/Brand/Group
    // pickers — see PeoplePage.svelte). Left unset, this behaves exactly as
    // before: search across every kind, quick-create defaults to `company`
    // but stays a free choice via the dialog's Type selector.
    kind?: CompanyLookup['kind'];
    defaultKind?: CompanyLookup['kind'];
    // Fired with the full picked row whenever an existing company is chosen
    // from the dropdown (not on quick-create/clear) — lets a caller react to
    // fields companyLookupJSON returns beyond id/kind/vertical_id/name, e.g.
    // the Hotels hotel picker auto-filling Brand/Group from a hotel's
    // brand_company_id/owner_company_id (see PeoplePage.svelte).
    onSelect?: (c: CompanyLookup) => void;
    id?: string;
    placeholder?: string;
  } = $props();

  const queryClient = useQueryClient();

  let query = $state('');
  let open = $state(false);
  // Syncs the visible text whenever `value` changes to something this
  // component didn't itself just set (edit-mode initial load, or a caller
  // programmatically re-assigning the bound value — e.g. the Hotels people
  // form auto-filling Brand/Group from a picked hotel's onSelect, see
  // PeoplePage.svelte) and the caller's `companies` pool has a matching row.
  // If the pool doesn't (yet) have it, this simply re-runs on the next
  // reactive tick rather than clobbering `query` with an empty string.
  // pick()/clear()/quick-create set `query` themselves and don't need this
  // (their target id is usually outside `companies`, a caller-prefetched
  // pool, so the `!selected` guard below leaves their own text alone).
  let lastSyncedValue: number | undefined = undefined;
  $effect(() => {
    const v = value;
    if (v === lastSyncedValue) return;
    const selected = companies.find((c) => c.id === v);
    if (v !== undefined && !selected) return;
    query = selected?.name ?? '';
    selectedKind = selected?.kind;
    selectedVerticalId = selected?.vertical_id;
    selectedName = selected?.name;
    lastSyncedValue = v;
  });

  const trimmedQuery = $derived(query.trim());
  let debouncedQuery = $state('');
  $effect(() => {
    const q = trimmedQuery;
    const handle = setTimeout(() => (debouncedQuery = q), 250);
    return () => clearTimeout(handle);
  });

  const searchQuery = createQuery(() => ({
    queryKey: ['company-search', { q: debouncedQuery, verticalId, kind }],
    queryFn: () =>
      companiesLookupApi.list({ q: debouncedQuery || undefined, per_page: 8, vertical_id: verticalId, kind }),
    enabled: open
  }));
  const results = $derived(searchQuery.data?.results ?? []);
  const hasExactMatch = $derived(
    results.some((c) => c.name.toLowerCase() === trimmedQuery.toLowerCase())
  );
  const showCreateOption = $derived(trimmedQuery.length > 0 && !hasExactMatch);

  // Display word for the quick-create prompt/dialog when `kind` locks the
  // picker to a single kind — 'owner' reads as "group" in the Hotels
  // hierarchy UI (see PeoplePage.svelte's Group picker).
  const KIND_LABEL: Record<CompanyLookup['kind'], string> = {
    company: 'company',
    hotel: 'hotel',
    brand: 'brand',
    owner: 'group'
  };

  function pick(c: CompanyLookup) {
    value = c.id;
    selectedKind = c.kind;
    selectedVerticalId = c.vertical_id;
    selectedName = c.name;
    query = c.name;
    open = false;
    onSelect?.(c);
  }

  function clear() {
    value = undefined;
    selectedKind = undefined;
    selectedVerticalId = undefined;
    selectedName = undefined;
    query = '';
    open = false;
  }

  function onBlur() {
    // Delayed so a mousedown on a dropdown option (or the create button)
    // registers before the dropdown is torn down.
    setTimeout(() => {
      open = false;
      const selected = results.find((c) => c.id === value) ?? companies.find((c) => c.id === value);
      if (selected) query = selected.name;
      else if (value === undefined) query = '';
    }, 120);
  }

  // Upper-cases each word's first letter, never lower-cases anything, so an
  // already-capitalized acronym survives: "ITC gardenia" -> "ITC Gardenia".
  // Mirrors titleCaseWords on the backend (internal/api/handlers/crm_helpers.go),
  // which is the source of truth — this is just so the user sees (and can
  // still override) the normalized result before confirming.
  function titleCaseWords(s: string): string {
    return s
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .map((w) => w[0].toUpperCase() + w.slice(1))
      .join(' ');
  }

  let showCreateDialog = $state(false);
  let createName = $state('');
  let createCity = $state('');
  let createKind = $state<CompanyLookup['kind']>('company');
  let createError = $state('');
  const createKindLabel = $derived(KIND_LABEL[kind ?? createKind]);

  function openCreateDialog() {
    createName = trimmedQuery;
    createCity = '';
    createKind = defaultKind;
    createError = '';
    showCreateDialog = true;
  }

  function normalizeCreateName() {
    createName = titleCaseWords(createName);
  }

  const quickCreateMutation = createMutation(() => ({
    mutationFn: () =>
      companiesQuickCreateApi.create(
        { name: createName.trim(), kind: kind ?? createKind, city: createCity.trim() || undefined },
        verticalId
      ),
    onSuccess: (company) => {
      queryClient.invalidateQueries({ queryKey: ['company-search'] });
      queryClient.invalidateQueries({ queryKey: ['companies-lookup'] });
      value = company.id;
      selectedKind = company.kind;
      // Not part of quickCreateCompany's minimal response (id/name/kind) —
      // the context vertical this combobox created into is the only
      // sensible answer anyway (the caller supplied it via `verticalId`).
      selectedVerticalId = verticalId;
      selectedName = company.name;
      query = company.name;
      showCreateDialog = false;
      open = false;
    },
    onError: (err: unknown) => (createError = getErrorMessage(err))
  }));

  function confirmCreate(e: SubmitEvent) {
    e.preventDefault();
    if (!createName.trim()) {
      createError = 'Name is required';
      return;
    }
    createError = '';
    quickCreateMutation.mutate();
  }
</script>

<div class="relative">
  <div class="relative">
    <input
      {id}
      class="input pr-8"
      bind:value={query}
      {placeholder}
      autocomplete="off"
      onfocus={() => (open = true)}
      onblur={onBlur}
    />
    <ChevronDown class="pointer-events-none absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-dark-gray" />
  </div>
  {#if open}
    <div
      class="absolute left-0 right-0 top-full z-30 mt-1.5 max-h-72 overflow-y-auto rounded-[11px] border border-toyama-cloudy-gray bg-white p-1.5 shadow-[var(--tym-shadow-3)]"
    >
      {#if value !== undefined}
        <button
          type="button"
          class="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left hover:bg-toyama-light-carbon"
          onmousedown={clear}
        >
          <span class="flex-1 text-[13px] font-semibold text-navy">-- none --</span>
        </button>
      {/if}
      {#each results as c (c.id)}
        <!-- svelte-ignore a11y_mouse_events_have_key_events -->
        <button
          type="button"
          class="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left hover:bg-toyama-light-carbon"
          onmousedown={() => pick(c)}
        >
          <span class="min-w-0 flex-1">
            <span class="block truncate text-[13px] font-medium text-navy">{c.name}</span>
            {#if c.city}<span class="block truncate text-[11px] text-dark-gray">{c.city}</span>{/if}
          </span>
          <span
            class="shrink-0 rounded-[5px] px-1.5 py-0.5 text-[10px] font-semibold capitalize"
            style="background:#F1F0EF; color:#666">{c.kind}</span
          >
        </button>
      {/each}
      {#if results.length === 0 && !showCreateOption}
        <div class="px-2.5 py-3.5 text-center text-[12.5px] text-dark-gray">No matches.</div>
      {/if}
      {#if showCreateOption}
        <button
          type="button"
          class="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-toyama-teal hover:bg-toyama-light-carbon"
          onmousedown={openCreateDialog}
        >
          <Plus class="h-3.5 w-3.5 shrink-0" />
          <span class="flex-1 truncate text-[13px] font-semibold"
            >+ Add &quot;{trimmedQuery}&quot; as new {createKindLabel}</span
          >
        </button>
      {/if}
    </div>
  {/if}
</div>

{#if showCreateDialog}
  <Modal title="Add company" onClose={() => (showCreateDialog = false)}>
    <form class="space-y-4" onsubmit={confirmCreate}>
      <div>
        <label class="label" for="quick-company-name">Company name</label>
        <input
          id="quick-company-name"
          class="input"
          bind:value={createName}
          onblur={normalizeCreateName}
          required
        />
      </div>
      <div>
        <label class="label" for="quick-company-city">Location</label>
        <input
          id="quick-company-city"
          class="input"
          bind:value={createCity}
          placeholder="City / area"
        />
      </div>
      {#if !kind}
        <div>
          <label class="label" for="quick-company-kind">Type</label>
          <select id="quick-company-kind" class="input" bind:value={createKind}>
            <option value="hotel">Hotel</option>
            <option value="company">Company</option>
            <option value="brand">Brand</option>
            <option value="owner">Owner</option>
          </select>
        </div>
      {/if}
      {#if createError}<p class="text-sm text-red-600">{createError}</p>{/if}
      <div class="flex justify-end gap-2 pt-2">
        <button type="button" class="btn-secondary" onclick={() => (showCreateDialog = false)}>Cancel</button>
        <button type="submit" class="btn-primary" disabled={quickCreateMutation.isPending}>
          {quickCreateMutation.isPending ? 'Adding...' : 'Confirm'}
        </button>
      </div>
    </form>
  </Modal>
{/if}
